import type { SeedingProductOptionsItem } from '@musinsa/services-curator';
import type { SeedingProductSlotMember } from '../types';

/**
 * 시딩 상품 옵션 어댑터 — 실 `getSeedingProductOptions`(상품별 option group + skus + extraOptions) 를
 * 옵션 시트가 **옵션 종류(색상/사이즈)와 무관하게 depth(1뎁스/2뎁스) 기준으로** 렌더할 수 있는
 * VM 으로 변환하고, 선택한 valueId 조합을 skuId(=goodsOptionNo) 로 해석한다.
 *
 * ── 실 API 의미(반영) ──
 * - 옵션 값은 캠페인 생성 시 저장된 `allowedOptions` snapshot 범위만 응답에 포함된다.
 * - SKU 품절은 시딩 정책 기준(무신사 Promise FC1→FC2 / 29CM UDH) 으로 BE 가 산정해 `outOfStock` 로 내려준다.
 * - 추가옵션(extraOptions, 쇼핑백 등)은 무신사 한정 — 29CM 은 항상 빈 배열.
 * - 캠페인 소속 아닌 goodsNo 는 BE 가 silent 제외(위변조 방어) → 응답 items 에 없을 수 있다.
 */

/* ── 시트 VM (옵션 종류 무관, 일반 group/sku 구조) ───────────────────── */

export interface OptionSheetValue {
  valueId: number;
  label: string;
  /** BE 컬러 코드(hex 아님). swatch 색 표현엔 미사용 — imageUrl 없을 때 흰색 폴백 여부만 판정. */
  color?: string;
  /** swatch 색 표현의 유일한 기준(컬러 썸네일 URL). 협의(2026-06): color 대신 imageUrl 만 사용. */
  imageUrl?: string;
}

export interface OptionSheetGroup {
  /** 옵션 그룹명(예: "색상" / "사이즈" / 임의) — placeholder·라벨로 그대로 사용. */
  optionName: string;
  values: OptionSheetValue[];
}

export interface OptionSheetSku {
  skuId: number;
  /** 이 SKU 를 구성하는 각 group 의 valueId 들. */
  valueIds: number[];
  outOfStock: boolean;
}

export interface OptionSheetExtraValue {
  valueId: number;
  label: string;
  extraPrice: number;
  soldOut: boolean;
}

export interface OptionSheetExtra {
  extraOptionId: number;
  name: string;
  required: boolean;
  values: OptionSheetExtraValue[];
}

export interface OptionSheetProduct {
  /** 시트 내부 key — String(goodsNo). */
  productId: string;
  goodsNo: number;
  brand: string;
  name: string;
  price: number;
  imageUrl: string;
  /** 색상별 품절 여부 — 아더컬러 슬롯에서 품절 색상을 비선택(dim+"품절")으로 표시. */
  soldOut: boolean;
  /** 옵션 그룹들 — length 가 곧 depth(1뎁스/2뎁스). 옵션 없는 상품은 빈 배열. */
  optionGroups: OptionSheetGroup[];
  skus: OptionSheetSku[];
  /** 추가옵션 — 무신사만 비어있지 않음. */
  extraOptions: OptionSheetExtra[];
}

/** 시트 선택 완료 결과 — 상품 카드 표시 + 수락/신청 payload(selectedOptions 원소). */
export interface ResolvedSelectedOption {
  goodsNo: number;
  /** 선택 SKU = body.selectedOptions[].goodsOptionNo. 단품(옵션 없는 상품)은 미설정. (SID-4296) */
  goodsOptionNo?: number;
  /** 선택한 추가옵션 valueId 들 (무신사). 29CM 은 빈 배열. */
  extraOptionNos: number[];
}

/**
 * 단품(옵션 없는 29CM 상품) 판정 — 옵션 조회 item 이 present 이면서 options·skus 가 모두 빈 배열.
 * (미로딩/캠페인 소속 아님으로 item==null 인 경우는 단품이 아니라 "미확인" 이므로 제외.) (SID-4296/4314)
 */
export const isSingleProductItem = (item?: SeedingProductOptionsItem): boolean =>
  item != null && item.options.length === 0 && item.skus.length === 0;

export interface OptionSheetConfirm {
  selected: ResolvedSelectedOption;
  /** 상품 카드 표시용 라벨 (예: "블랙 · M" / "M" / "블랙 · M · 쇼핑백"). */
  label: string;
}

/* ── 변환 ─────────────────────────────────────────────────────────── */

const toGroup = (group: SeedingProductOptionsItem['options'][number]): OptionSheetGroup => ({
  optionName: group.optionName,
  values: group.values.map((value) => ({
    valueId: value.valueId,
    label: value.name,
    color: value.color ?? undefined,
    imageUrl: value.imageUrl ?? undefined,
  })),
});

const toExtra = (extra: SeedingProductOptionsItem['extraOptions'][number]): OptionSheetExtra => ({
  extraOptionId: extra.extraOptionId,
  name: extra.name,
  required: extra.required,
  values: extra.values.map((value) => ({
    valueId: value.valueId,
    label: value.name,
    extraPrice: value.price,
    soldOut: value.outOfStock,
  })),
});

/**
 * 슬롯 멤버 goods(meta) + 옵션(실 API item) 병합 → 시트 VM.
 * 단일 슬롯이면 멤버 1개(곧바로 옵션), 아더컬러 슬롯이면 멤버 N개(상품 선택 → 옵션).
 * item 이 없으면(미로딩/캠페인 소속 아님) optionGroups·skus 빈 배열(옵션 없는 상품 취급).
 */
export const buildOptionSheetProducts = (
  members: SeedingProductSlotMember[],
  itemByGoodsNo: Map<number, SeedingProductOptionsItem>,
): OptionSheetProduct[] =>
  members.map((member) => {
    const item = itemByGoodsNo.get(member.goodsNo);
    return {
      productId: String(member.goodsNo),
      goodsNo: member.goodsNo,
      brand: member.brand,
      name: member.name,
      price: member.price,
      imageUrl: member.imageUrl,
      soldOut: member.soldOut,
      optionGroups: item ? item.options.map(toGroup) : [],
      skus: item
        ? item.skus.map((sku) => ({
            skuId: sku.skuId,
            valueIds: sku.valueIds,
            outOfStock: sku.outOfStock,
          }))
        : [],
      extraOptions: item ? item.extraOptions.map(toExtra) : [],
    };
  });

/* ── SKU 해석 / 품절 cascade (depth 무관) ─────────────────────────── */

const skusMatching = (skus: OptionSheetSku[], valueIds: number[]): OptionSheetSku[] =>
  skus.filter((sku) => valueIds.every((valueId) => sku.valueIds.includes(valueId)));

/**
 * prior 그룹 선택 하에서 특정 group value 의 노출 가부 + 품절 여부.
 * - available=false : prior + value 조합을 가진 SKU 가 아예 없음(allowedOptions 밖) → 노출 제외.
 * - soldOut=true    : 조합 SKU 는 있으나 전부 outOfStock.
 */
export const valueAvailability = (
  skus: OptionSheetSku[],
  priorValueIds: number[],
  valueId: number,
): { available: boolean; soldOut: boolean } => {
  const matched = skusMatching(skus, [...priorValueIds, valueId]);
  if (matched.length === 0) return { available: false, soldOut: true };
  return { available: true, soldOut: matched.every((sku) => sku.outOfStock) };
};

/** 전체 그룹 선택 valueIds → 정확히 일치하는 SKU. 옵션 없는 상품은 valueIds=[] 단일 SKU. */
export const resolveSku = (
  skus: OptionSheetSku[],
  selectedValueIds: number[],
): OptionSheetSku | null =>
  skus.find(
    (sku) =>
      sku.valueIds.length === selectedValueIds.length &&
      selectedValueIds.every((valueId) => sku.valueIds.includes(valueId)),
  ) ?? null;
